import React, { useState, useEffect } from "react";
import { v4 as uuidV4 } from "uuid";

import { useParams } from "react-router-dom";
import { VictoryLine, VictoryScatter } from "victory";

// API configuration
import useAxios from "../../../../../hooks/useAxios";
import jsonFormatAuth from "../../../../../apis/jsonFormatAuth";
import { sanitizePasiStatisticsEntries } from "../../../../../helpers/apiUtils/pasiStatisticsUtils";

import BaseLineChart from "./BaseLineChart";
import { Heading1 } from "../../../../../components/Headings/Headings";

// Icons
import { ChartBarSquareIcon, WindowIcon } from "@heroicons/react/24/outline";

const ToggleGraphButton = ({
  title,
  part,
  chartVisibility,
  setFunction,
  toggleVisibility,
  handleMouseEnter,
  handleMouseLeave,
}) => {
  return (
    <div
      onClick={() => {
        toggleVisibility({
          setFunction: setFunction,
          part: part,
        });
      }}
      onMouseEnter={() => {
        handleMouseEnter({
          setFunction: setFunction,
          part: part,
        });
      }}
      onMouseLeave={() => {
        handleMouseLeave({
          setFunction: setFunction,
          part: part,
        });
      }}
      className={`flex flex-1 flex-row items-center justify-center w-full p-2
                  rounded-lg select-none
                  bg-white hover:bg-cyan-100
                  border border-1 border-neutral-300
                  text-xl font-medium text-neutral-800

                  duration-300

                  ${chartVisibility[part].isVisible ? "" : "opacity-50"}
                 `}
    >
      {title}
    </div>
  );
};

const PasiStatistics = () => {
  const hoverStroke = 7;
  const nonHoverStroke = 3;
  const hoverPointSize = 10;
  const nonHoverPointSize = 4;
  const hoverTextSize = 25;
  const nonHoverTextSize = 10;

  // Line colors
  const headColor = "#ef4444";
  const armsColor = "#84cc16";
  const torsoColor = "#14b8a6";
  const legsColor = "#8b5cf6";
  const frontColor = "#f97316";
  const backColor = "#3b82f6";
  const bodyColor = "#d946ef";

  const [data, setData] = useState([]);

  // API START
  const { id } = useParams();
  // API related
  const { response, error, axiosFetch } = useAxios();
  const apiUrl = `entries/pasi_statistics/${id}/`;

  // FUNCTIONS START
  useEffect(() => {
    // Obtaining data from API on initial page load
    getStatisticsData();
  }, []);

  const getStatisticsData = () => {
    axiosFetch({
      axiosInstance: jsonFormatAuth(),
      method: "GET",
      url: apiUrl,
      sendSignal: false,
      requestConfig: {},
    });
  };

  // RESPONSE Detection
  useEffect(() => {
    if (response) {
      // console.log("Response:");
      // console.log(response);
      const actionCode = response?.action_code;
      if (actionCode === "statistics_entries_sent") {
        const statisticsEntriesResponse = response?.payload?.statistics_entries;
        // console.log("statisticsEntriesResponse");
        // console.log(statisticsEntriesResponse);

        if (statisticsEntriesResponse) {
          const sanitizedStatisticsEntriesResponse =
            sanitizePasiStatisticsEntries({
              statisticsEntriesResponse: statisticsEntriesResponse,
            });

          // console.log("sanitizedStatisticsEntriesResponse");
          // console.log(sanitizedStatisticsEntriesResponse);

          setData(sanitizedStatisticsEntriesResponse);
        }
      }
    }
  }, [response]);

  // ERROR Detection
  // useEffect(() => {
  //   if (error) {
  //     // console.log("Error:");
  //     // console.log(error);

  //     const actionCode = error?.action_code;
  //   }
  // }, [error]);

  // API END

  // Chart config START
  const bodyParts = ["head", "arms", "torso", "legs", "front", "back", "body"];

  // Initialize state object with all lines set to show
  const initialLineState = bodyParts.reduce((acc, part) => {
    acc[part] = { isVisible: true, isHovering: false };
    return acc;
  }, {});

  const [affectedPercentageVisibility, setAffectedPercentageVisibility] =
    useState(initialLineState);
  const [areaScoreVisibility, setAreaScoreVisibility] =
    useState(initialLineState);
  const [erythemaVisibility, setErythemaVisibility] =
    useState(initialLineState);
  const [indurationVisibility, setIndurationVisibility] =
    useState(initialLineState);
  const [desquamationVisibility, setDesquamationVisibility] =
    useState(initialLineState);
  const [scoreVisibility, setScoreVisibility] = useState(initialLineState);

  const toggleVisibility = ({ setFunction, part }) => {
    setFunction((prevState) => ({
      ...prevState,
      [part]: {
        ...prevState[part],
        isVisible: !prevState[part].isVisible,
      },
    }));
  };

  const handleMouseEnter = ({ setFunction, part }) => {
    setFunction((prevState) => ({
      ...prevState,
      [part]: {
        ...prevState[part],
        isHovering: true,
      },
    }));
  };

  const handleMouseLeave = ({ setFunction, part }) => {
    setFunction((prevState) => ({
      ...prevState,
      [part]: {
        ...prevState[part],
        isHovering: false,
      },
    }));
  };

  const chartsConfig = [
    {
      yAxisLabel: "Affected Percentage",
      figureCaption: "Affected percentages over time",
      chartVisibility: affectedPercentageVisibility,
      setChartVisibility: setAffectedPercentageVisibility,
      fieldEnding: "affected_percentage",
      yAxisValues: null,
      renderHead: true,
      renderArms: true,
      renderTorso: true,
      renderLegs: true,
      renderFront: true,
      renderBack: true,
      renderBody: true,
    },

    {
      yAxisLabel: "Area Score",
      figureCaption: "Area Scores over time",
      chartVisibility: areaScoreVisibility,
      setChartVisibility: setAreaScoreVisibility,
      fieldEnding: "area_score",
      yAxisValues: null,
      renderHead: true,
      renderArms: true,
      renderTorso: true,
      renderLegs: true,
      renderFront: false,
      renderBack: false,
      renderBody: false,
    },

    {
      yAxisLabel: "Erythema",
      figureCaption: "Erythema over time",
      chartVisibility: erythemaVisibility,
      setChartVisibility: setErythemaVisibility,
      fieldEnding: "erythema",
      yAxisValues: [0, 1, 2, 3, 4],
      renderHead: true,
      renderArms: true,
      renderTorso: true,
      renderLegs: true,
      renderFront: false,
      renderBack: false,
      renderBody: false,
    },
    {
      yAxisLabel: "Induration",
      figureCaption: "Induration over time",
      chartVisibility: indurationVisibility,
      setChartVisibility: setIndurationVisibility,
      fieldEnding: "induration",
      yAxisValues: [0, 1, 2, 3, 4],
      renderHead: true,
      renderArms: true,
      renderTorso: true,
      renderLegs: true,
      renderFront: false,
      renderBack: false,
      renderBody: false,
    },
    {
      yAxisLabel: "Desquamation",
      figureCaption: "Desquamation over time",
      chartVisibility: desquamationVisibility,
      setChartVisibility: setDesquamationVisibility,
      fieldEnding: "desquamation",
      yAxisValues: [0, 1, 2, 3, 4],
      renderHead: true,
      renderArms: true,
      renderTorso: true,
      renderLegs: true,
      renderFront: false,
      renderBack: false,
      renderBody: false,
    },

    {
      yAxisLabel: "Scores",
      figureCaption: "Segment scores over time",
      chartVisibility: scoreVisibility,
      setChartVisibility: setScoreVisibility,
      fieldEnding: "segment_score",
      yAxisValues: null,
      renderHead: true,
      renderArms: true,
      renderTorso: true,
      renderLegs: true,
      renderFront: false,
      renderBack: false,
      renderBody: false,
    },
  ];

  const bodyPartsConfig = [
    { part: "head", title: "Head" },
    { part: "arms", title: "Torso" },
    { part: "torso", title: "Legs" },
    { part: "legs", title: "Arms" },
    { part: "front", title: "Body Front" },
    { part: "back", title: "Body Back" },
    { part: "body", title: "Whole Body" },
  ];

  const getDataKey = ({ part, fieldEnding }) => {
    // front, back, body are only used for affected_percentage, and have different names structure.
    if (part === "front") {
      return "affected_percentage_front";
    } else if (part === "back") {
      return "affected_percentage_back";
    } else if (part === "body") {
      return "affected_percentage_body";
    } else {
      return `${part}_${fieldEnding}`;
    }
  };

  const partColors = {
    head: headColor,
    arms: armsColor,
    torso: torsoColor,
    legs: legsColor,
    front: frontColor,
    back: backColor,
    body: bodyColor,
  };

  // Chart config END

  return (
    <>
      <Heading1 headingText={"PASI Statistics"} />

      <div className="py-2" />

      {data.length !== 0 ? (
        <>
          {/* PASI Score START */}
          <div className="hidden md:flex flex-col w-full space-y-4">
            <BaseLineChart
              data={data}
              minYDomain={-0.5}
              // maxYDomain={72}
              // yAxisValues={[
              //   0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70,
              // ]}
              yAxisLabel={"PASI Score"}
              figureCaption={"PASI scores over time"}
            >
              <VictoryLine
                style={{
                  data: {
                    stroke: bodyColor,
                    strokeWidth: hoverStroke,
                    strokeLinecap: "round",
                  },
                }}
                interpolation="natural"
                data={data.map((d, i) => ({
                  x: i + 1,
                  y: d.pasi_score,
                }))}
              />
              <VictoryScatter
                size={hoverPointSize}
                style={{
                  data: {
                    fill: bodyColor,
                  },
                  labels: {
                    fontSize: hoverTextSize,
                    fontWeight: 600,
                    padding: 20,
                  },
                }}
                labels={({ datum }) => datum.y}
                data={data.map((d, i) => ({
                  x: i + 1,
                  y: d.pasi_score,
                }))}
              />
            </BaseLineChart>

            {/* PASI Score END */}

            {chartsConfig.map((chartConfig, index) => (
              <BaseLineChart
                key={`${uuidV4()}-${index}`}
                data={data}
                minYDomain={-0.5}
                yAxisValues={chartConfig.yAxisValues && chartConfig.yAxisValues}
                yAxisLabel={chartConfig.yAxisLabel}
                figureCaption={chartConfig.figureCaption}
                visibilityController={
                  <div className="flex flex-row items-center justify-center w-full py-4 space-x-2 px-8">
                    {bodyPartsConfig.map((bodyPartConfig) =>
                      chartConfig[
                        `render${
                          bodyPartConfig.part.charAt(0).toUpperCase() +
                          bodyPartConfig.part.slice(1)
                        }`
                      ] ? (
                        <ToggleGraphButton
                          key={`${uuidV4()}-${index}`}
                          title={bodyPartConfig.title}
                          part={bodyPartConfig.part}
                          chartVisibility={chartConfig.chartVisibility}
                          setFunction={chartConfig.setChartVisibility}
                          toggleVisibility={toggleVisibility}
                          handleMouseEnter={handleMouseEnter}
                          handleMouseLeave={handleMouseLeave}
                        />
                      ) : null
                    )}
                  </div>
                }
              >
                {bodyPartsConfig.map((bodyPartConfig) =>
                  chartConfig[
                    `render${
                      bodyPartConfig.part.charAt(0).toUpperCase() +
                      bodyPartConfig.part.slice(1)
                    }`
                  ]
                    ? [
                        // Line plot
                        chartConfig.chartVisibility[`${bodyPartConfig.part}`]
                          .isVisible && (
                          <VictoryLine
                            key={`${uuidV4()}-${index}`}
                            style={{
                              data: {
                                stroke: partColors[bodyPartConfig.part],
                                strokeWidth: chartConfig.chartVisibility[
                                  `${bodyPartConfig.part}`
                                ].isHovering
                                  ? hoverStroke
                                  : nonHoverStroke,
                                strokeLinecap: "round",
                              },
                            }}
                            interpolation="natural"
                            data={data.map((d, i) => ({
                              x: i + 1,
                              y: d[
                                getDataKey({
                                  part: bodyPartConfig.part,
                                  fieldEnding: chartConfig.fieldEnding,
                                })
                              ],
                            }))}
                          />
                        ),
                        // Scatter plot
                        chartConfig.chartVisibility[`${bodyPartConfig.part}`]
                          .isVisible && (
                          <VictoryScatter
                            key={`${uuidV4()}-${index}`}
                            size={
                              chartConfig.chartVisibility[
                                `${bodyPartConfig.part}`
                              ].isHovering
                                ? hoverPointSize
                                : nonHoverPointSize
                            }
                            style={{
                              data: {
                                fill: partColors[bodyPartConfig.part],
                              },
                              labels: chartConfig.chartVisibility[
                                `${bodyPartConfig.part}`
                              ].isHovering
                                ? {
                                    fontSize: hoverTextSize,
                                    fontWeight: 600,
                                    padding: 20,
                                  }
                                : {
                                    fontSize: nonHoverTextSize,
                                    padding: 20,
                                  },
                            }}
                            labels={({ datum }) => datum.y}
                            data={data.map((d, i) => ({
                              x: i + 1,
                              y: d[
                                `${getDataKey({
                                  part: bodyPartConfig.part,
                                  fieldEnding: chartConfig.fieldEnding,
                                })}`
                              ],
                            }))}
                          />
                        ),
                      ]
                    : null
                )}
              </BaseLineChart>
            ))}
          </div>

          <div className="md:hidden flex">
            <div className="flex flex-col items-center justify-center w-full pt-20 Xbg-red-500">
              <WindowIcon className="h-72 w-72 text-neutral-800 stroke-1" />
              <p className="text-center leading-relaxed text-2xl font-medium text-neutral-800 ">
                Please open this page on a larger device to view PASI
                Statistics.
              </p>
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center w-full py-40 Xbg-red-500">
          <ChartBarSquareIcon className="h-96 w-96 text-neutral-800 stroke-1" />
          <div className="max-w-2xl">
            <p className="text-center leading-relaxed text-4xl font-medium text-neutral-800 ">
              Create some Psoriasis Segmentation entries first to view PASI
              Statistics.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default PasiStatistics;
